import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { AfterViewInit, Component, EventEmitter, HostListener, Inject, Input, OnDestroy, Output, ViewChild } from '@angular/core';
import { MatSlider } from '@angular/material';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { UserService } from 'app/shared/services/user.service';
import { Subject } from 'rxjs';

@Component({
  selector: 'app-video-player',
  templateUrl: './video-player.component.html',
  styleUrls: ['./video-player.component.scss'],
})
export class VideoPlayerComponent implements AfterViewInit, OnDestroy {
  static videoVolume: number = 0.5;
  static videoMuted: boolean = false;

  event: MouseEvent;
  clientX = 0;
  clientY = 0;

  @Input() video: string | SafeUrl;
  @Input() title: string;
  @Input() isNextEnabled: boolean;
  @Input() isShuffleEnabled: boolean;
  @Input() isLastVideo: boolean;
  @Input() captions: string;

  @Output() getNextExercise: EventEmitter<any> = new EventEmitter();
  @Output() getNewExercise: EventEmitter<any> = new EventEmitter();
  @Output() onVideoEnded: EventEmitter<any> = new EventEmitter();
  @Output() videoProgress: EventEmitter<any> = new EventEmitter();

  @ViewChild('videoPlayer') videoPlayer: any;
  @ViewChild('vidContainer') vidContainer: any;
  @ViewChild('volumeSlider') volumeSlider: MatSlider;

  public now: number;
  public isMuted = VideoPlayerComponent.videoMuted;
  private seekbarMaxValue: number = 100;
  public seekbarProgress: number = 0;
  private seekbarThumb: number = 0;
  public fullScreened: boolean = false;
  public showOverlay: boolean = true;
  public videoEnded: boolean = false;
  public pausedVideo: boolean = true;
  public captionObjectUrl: string | SafeUrl = '';
  public captionActive: boolean = false;
  private mouseOverTimer: any;

  private ngUnsubscribe: Subject<void> = new Subject<void>();

  constructor(@Inject(DOCUMENT) private document: any, private httpClient: HttpClient, private sanitizer: DomSanitizer, private userService: UserService) {}

  ngAfterViewInit() {
    this.fetchCaptionFile();

    const i = setInterval(() => {
      if (this.videoPlayer.nativeElement.readyState > 0) {
        this.now = this.videoPlayer.nativeElement.duration - this.videoPlayer.nativeElement.currentTime;
        clearInterval(i);
      }
    }, 100);
    this.playPause();

    this.volumeSlider.value = VideoPlayerComponent.videoVolume;
    this.videoPlayer.nativeElement.volume = this.volumeSlider.value;
    this.videoPlayer.nativeElement.muted = this.isMuted;

    // Read the user setting for captions and activate or deactivate them on video start
    this.userService.currentUser.takeUntil(this.ngUnsubscribe).subscribe(user => {
      const caption = this.videoPlayer.nativeElement.textTracks[0];

      if (user.enableCaptions) {
        caption.mode = 'showing';
        this.captionActive = true;
      } else {
        caption.mode = 'hidden';
        this.captionActive = false;
      }
    });
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  isOverlayVisible(): boolean {
    return this.showOverlay || this.videoEnded || this.pausedVideo;
  }

  playPause() {
    if (this.videoPlayer.nativeElement.paused) {
      this.videoPlayer.nativeElement.play();
      this.pausedVideo = false;
      this.showOverlay = false;
      this.videoEnded = false;
    } else {
      this.videoPlayer.nativeElement.pause();
      this.pausedVideo = true;
    }
  }

  seekBar(event: any) {
    // Calculate the new time
    const time = this.videoPlayer.nativeElement.duration * (event.target.value / 100);

    // Update the video time
    this.videoPlayer.nativeElement.currentTime = time;
  }

  seekBarUpdate() {
    // Calculate the slider value
    const value = (100 / this.videoPlayer.nativeElement.duration) * this.videoPlayer.nativeElement.currentTime;

    // Update the slider value
    this.seekbarProgress = value;

    this.now = this.videoPlayer.nativeElement.duration - this.videoPlayer.nativeElement.currentTime;

    this.videoEnded = false;
    if (this.now === 0) {
      this.videoEnded = true;
      this.pausedVideo = true;
      /**
      * Stop Fullscreen on iPhone when Video ends to display Video Overlay (-Buttons)
      */
      if (window.navigator.userAgent && window.navigator.userAgent.includes('iPhone')) {
        //this.toggleFullScreen();
        this.videoPlayer.nativeElement.webkitExitFullscreen();
      }
      this.onVideoEnded.emit();
    }

    this.videoProgress.emit({
      value: this.videoPlayer.nativeElement.currentTime,
      progress: value,
      total: this.videoPlayer.nativeElement.duration,
    });
  }

  changeUrl(videoUrl: string | SafeUrl, captionUrl: string) {
    this.video = videoUrl;
    this.videoPlayer.nativeElement.src = videoUrl;
    this.captions = captionUrl;
    this.fetchCaptionFile();
    this.playPause();
  }

  mute() {
    this.videoPlayer.nativeElement.muted = !this.videoPlayer.nativeElement.muted;
    this.isMuted = VideoPlayerComponent.videoMuted = this.videoPlayer.nativeElement.muted;

    if (this.isMuted) {
      this.volumeSlider.value = 0;
    } else {
      this.volumeSlider.value = VideoPlayerComponent.videoVolume;
    }
  }

  volumeBar(event) {
    this.videoPlayer.nativeElement.volume = event.value;
    // persist video volume between players
    VideoPlayerComponent.videoVolume = event.value;
    if (event.value > 0 && this.isMuted) {
      this.mute();
    }
  }

  newExercise(event: MouseEvent) {
    event.stopPropagation();
    this.getNewExercise.emit();
  }

  nextExercise(event: MouseEvent) {
    event.stopPropagation();
    this.getNextExercise.emit();
  }

  /**
   * Whenever you switch videos without closing the vidoe dialog, it is possible,
   * that the subtitle of the previous video keeps showing up if captions are enabled.
   * Toggling the mode fixes this.
   */
  resetCaption() {
    const caption = this.videoPlayer.nativeElement.textTracks[0];

    if (caption.mode === 'showing') {
      caption.mode = 'hidden';
      caption.mode = 'showing';
    }
  }

  toggleCaption() {
    if (!this.captionObjectUrl) {
      return;
    }

    const caption = this.videoPlayer.nativeElement.textTracks[0];

    if (caption.mode === 'hidden') {
      caption.mode = 'showing';
      this.captionActive = true;
    } else {
      caption.mode = 'hidden';
      this.captionActive = false;
    }
  }

  toggleFullScreen() {
    const element = this.vidContainer.nativeElement;
    if (this.fullScreened) {
      if (this.document.cancelFullScreen) {
        this.document.cancelFullScreen();
      } else if (this.document.mozCancelFullScreen) {
        this.document.mozCancelFullScreen();
      } else if (this.document.webkitCancelFullScreen) {
        this.document.webkitCancelFullScreen();
      } else if (this.document.webkitExitFullscreen) {
        this.document.webkitExitFullscreen();
      } else if (this.document.msExitFullscreen) {
        this.document.msExitFullscreen();
      }
      /**
       * For iPone iOS only
       */
      else if (this.videoPlayer.nativeElement.webkitExitFullscreen) {
        this.videoPlayer.nativeElement.webkitExitFullscreen();
      }
    } else {
      if (element.requestFullScreen) {
        element.requestFullScreen();
      } else if (element.mozRequestFullScreen) {
        element.mozRequestFullScreen();
      } else if (element.webkitRequestFullScreen) {
        element.webkitRequestFullScreen();
      } else if (element.webkitRequestFullscreen) {
        element.webkitRequestFullscreen();
      } else if (element.msRequestFullscreen) {
        element.msRequestFullscreen();
      }
      /**
       * iOS (16.0) on iPhone does not Support Fullscreen for other Elements than <video> !!
       * Cannot display custom Overlay
       */
      else if (this.videoPlayer.nativeElement.webkitEnterFullScreen) {
        this.videoPlayer.nativeElement.webkitEnterFullScreen();
      }
    }
  }

  cancelFS() {}

  fetchCaptionFile() {
    this.httpClient.get(this.captions, { headers: { 'access-control-allow-origin': '*' }, responseType: 'blob' }).subscribe((content) => {
      if (content) {
        if (this.captionObjectUrl) {
          window.URL.revokeObjectURL(this.captionObjectUrl.toString());
        }
        this.captionObjectUrl = this.sanitizer.bypassSecurityTrustUrl(window.URL.createObjectURL(content));
        this.resetCaption();
      }
    });
  }

  @HostListener('document:fullscreenchange', ['$event'])
  @HostListener('document:webkitfullscreenchange', ['$event'])
  @HostListener('document:mozfullscreenchange', ['$event'])
  @HostListener('document:MSFullscreenChange', ['$event'])
  keyEvent(event: KeyboardEvent) {
    this.fullScreened =
      (document as any).fullscreen ||
      (document as any).mozFullScreen ||
      (document as any).webkitIsFullScreen ||
      (document as any).msFullscreenElement ||
      (document as any).webkitDisplayingFullscreen;
  }

  mouseOver(event: MouseEvent) {
    if (this.mouseOverTimer) {
      clearTimeout(this.mouseOverTimer);
    }
    this.showOverlay = true;

    this.mouseOverTimer = setTimeout(() => {
      this.showOverlay = false;
    }, 2000);
  }
  mouseOut(event: MouseEvent) {
    if (this.mouseOverTimer) {
      clearTimeout(this.mouseOverTimer);
    }
    this.showOverlay = true;

    this.mouseOverTimer = setTimeout(() => {
      this.showOverlay = false;
    }, 500);
  }
}



// WEBPACK FOOTER //
// /codebuild/output/src3658806670/src/client/web-client/src/app/theme/components/video-player/video-player.component.ts